/**
 * Copyright © 2026 Delicious AI, LLC
 *
 * Dialog to view and update a display's settings.
 */

import { useMutation } from '@apollo/client'
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'

import {
  UPDATE_DISPLAY,
  UpdateDisplayMutation,
  UpdateDisplayMutationVariables,
} from '@/graphql'
import useT from '@/i18n/useT'

export type DisplayAverageDepthDialogProps = {
  open: boolean
  onClose: () => void
  /** Display UUID */
  displayId: string
  /** Store UUID — required by `DisplayInput.store` */
  storeId: string
  displayName: string
  currentAverageDepth: number
  /** Called after a successful save (e.g. refetch parent queries) */
  onSaved?: () => void
}

const DisplaySettingsDialog: React.FC<DisplayAverageDepthDialogProps> = ({
  open,
  onClose,
  displayId,
  storeId,
  displayName,
  currentAverageDepth,
  onSaved,
}) => {
  const { t } = useT()
  const [draft, setDraft] = useState(String(currentAverageDepth))
  const [updateDisplay, { loading, error }] = useMutation<
    UpdateDisplayMutation,
    UpdateDisplayMutationVariables
  >(UPDATE_DISPLAY)
  useEffect(() => {
    if (open) {
      setDraft(String(currentAverageDepth))
    }
  }, [open, currentAverageDepth])

  const parsed = useMemo(() => {
    const n = Number.parseInt(draft, 10)
    return Number.isFinite(n) ? n : NaN
  }, [draft])

  const isValid = Number.isFinite(parsed) && parsed >= 1 && parsed <= 999
  const unchanged = parsed === currentAverageDepth
  const canSave = isValid && !unchanged && !loading

  const handleSave = async () => {
    if (!canSave) return
    try {
      await updateDisplay({
        variables: {
          data: {
            id: displayId,
            store: { set: storeId },
            averageDepth: parsed,
          },
        },
      })
      onSaved?.()
      onClose()
    } catch {
      // Apollo sets `error`; avoid unhandled rejection
    }
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {t('displaySettingsDialog.title', { name: displayName })}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 2 }}>
          <TextField
            label={t('displaySettingsDialog.averageDepthLabel')}
            type="number"
            value={draft}
            onChange={e => setDraft(e.target.value)}
            fullWidth
            disabled={loading}
            helperText={t('displaySettingsDialog.averageDepthHelperText')}
            error={draft !== '' && !isValid}
          />
          {error ? <Alert severity="error">{error.message}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          {t('common.cancel')}
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={!canSave}>
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default DisplaySettingsDialog
